// BoardView — Wave 2 Kanban. Status columns; drag a card to another column to
// change its status (native HTML5 DnD → PATCH). Filtered upstream by the shared
// filter bar (it just renders whatever `items` it's given). Closed hidden unless
// toggled on.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TYPE_META, STATUS_META } from "../../constants/enums";
import { TalmiaBadge } from "../Badges";
import { assigneeShort, assigneeLabel } from "../../lib/assignees";
import { AiPct } from "../items/LiveCheckPanel";

const BOARD_STATUSES = ["New", "Working", "Testing", "Blocked", "On Hold", "Done"];
const PRIORITY_DOT = {
  Critical: "bg-red-400", Urgent: "bg-red-400", High: "bg-orange-400",
  Medium: "bg-amber-400", Low: "bg-sky-400",
};

function Card({ it, usersById, onDragStart, onDragEnd, onCardClick }) {
  const typeIcon = TYPE_META[it.type]?.icon || "•";
  const assignee = assigneeLabel(it.assigned_to, usersById);
  return (
    <Link
      to={`/items/${it.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, it)}
      onDragEnd={onDragEnd}
      onClick={onCardClick}
      data-testid={`board-card-${it.id}`}
      className="block rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 p-3 cursor-grab active:cursor-grabbing transition-colors"
    >
      <div className="flex items-start gap-2">
        <span className="text-base leading-none mt-0.5">{typeIcon}</span>
        <span className="text-[13px] text-slate-100 line-clamp-2 flex-1">{it.title}</span>
      </div>
      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        <span className={`w-2 h-2 rounded-full ring-4 ring-white/5 ${PRIORITY_DOT[it.priority] || "bg-slate-400"}`} title={it.priority} />
        <TalmiaBadge value={it.talmia_version} />
        {it.phase && <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 border border-white/10 rounded px-1.5 py-0.5">{it.phase}</span>}
        {(it.roi !== null && it.roi !== undefined && it.roi !== "") && (
          <span className="text-[10px] font-mono text-emerald-300 border border-emerald-400/30 bg-emerald-500/10 rounded px-1.5 py-0.5">ROI {it.roi}</span>
        )}
        <AiPct v={it.ai_verdict} compact testid={`board-ai-pct-${it.id}`} />
        {assignee && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-300" title={assignee}>
            <span className="grid place-items-center w-5 h-5 rounded-full bg-white/10 border border-white/15 font-mono text-[9px]">
              {assigneeShort(it.assigned_to, usersById)}
            </span>
          </span>
        )}
      </div>
    </Link>
  );
}

export default function BoardView({ items, usersById, onPatchItem }) {
  const [showClosed, setShowClosed] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const draggedRef = useRef(false);   // true between dragstart and a fresh click

  const columns = showClosed ? [...BOARD_STATUSES, "Closed"] : BOARD_STATUSES;
  const byStatus = (s) => items.filter((it) => it.status === s);

  const onDragStart = (e, it) => {
    setDragId(it.id);
    draggedRef.current = true;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", it.id); } catch { /* older browsers */ }
  };
  const onDragEnd = () => {
    setDragId(null); setOverCol(null);
    // Reset just after any synthetic post-drop click has had a chance to fire,
    // so a later genuine click on another card still navigates normally.
    setTimeout(() => { draggedRef.current = false; }, 0);
  };
  // A drag ends with a synthetic click on the <Link>; swallow that one click
  // so the card doesn't navigate to the item after a drop.
  const onCardClick = (e) => {
    if (draggedRef.current) { e.preventDefault(); }
  };
  const onDrop = (e, status) => {
    e.preventDefault();
    const id = (e.dataTransfer && e.dataTransfer.getData("text/plain")) || dragId;
    if (id) {
      const it = items.find((x) => x.id === id);
      if (it && it.status !== status) onPatchItem(id, { status });
    }
    setDragId(null); setOverCol(null);
  };

  return (
    <div data-testid="board-view">
      <div className="flex items-center justify-end mb-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer" data-testid="board-show-closed">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show Closed
        </label>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3">
        {columns.map((status) => {
          const colItems = byStatus(status);
          const tone = STATUS_META[status]?.tone || "";
          return (
            <div
              key={status}
              onDragOver={(e) => { e.preventDefault(); setOverCol(status); }}
              onDragLeave={() => setOverCol((c) => (c === status ? null : c))}
              onDrop={(e) => onDrop(e, status)}
              data-testid={`board-col-${status.replace(/\s+/g, "-")}`}
              className={`shrink-0 w-[280px] rounded-xl border p-2.5 transition-colors ${
                overCol === status ? "border-orange-400/50 bg-orange-500/[0.04]" : "border-white/5 bg-white/[0.015]"
              }`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border ${tone}`}>
                  {status}
                </span>
                <span className="text-[11px] text-slate-500 font-mono">{colItems.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {colItems.map((it) => (
                  <Card key={it.id} it={it} usersById={usersById} onDragStart={onDragStart} onDragEnd={onDragEnd} onCardClick={onCardClick} />
                ))}
                {colItems.length === 0 && (
                  <div className="text-[11px] text-slate-600 text-center py-6 border border-dashed border-white/5 rounded-lg">
                    Drop here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
